import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { PhoneCallResponse, StructuredTranscriptEntryType } from '@trapeze/server';
import { CheckCircle2, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import * as React from 'react';

const formatTranscriptTime = (timeInSeconds: number): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const CollapsibleToolCall: React.FC<{
  toolName: string;
  timeSec: number | null;
  parsedArgs: Record<string, unknown> | null;
  isSpecialCall?: boolean;
}> = ({ toolName, timeSec, parsedArgs, isSpecialCall = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (parsedArgs) {
      navigator.clipboard.writeText(JSON.stringify(parsedArgs, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const borderColor = isSpecialCall
    ? 'border-l-amber-400 dark:border-l-amber-500'
    : 'border-l-indigo-400 dark:border-l-indigo-500';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-3">
      <div
        className={`border-l-4 ${borderColor} bg-slate-50/50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span
                className="text-slate-600 dark:text-slate-400 transition-transform"
                style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
              <span className="text-sm">🔧</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Tool Call: {toolName}
              </span>
              {timeSec !== null && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {timeSec.toFixed(2)}s
                </span>
              )}
            </div>
            {parsedArgs && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                title="Copy arguments"
              >
                {copied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800">
            {parsedArgs ? (
              <pre className="text-xs font-mono bg-white dark:bg-slate-950 p-3 rounded border border-slate-200 dark:border-slate-800 overflow-x-auto">
                {JSON.stringify(parsedArgs, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-muted-foreground italic">No arguments</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const CollapsibleToolResult: React.FC<{
  timeSec: number | null;
  parsedContent: Record<string, unknown> | null;
  rawContent: string | null;
  successful: boolean | null;
}> = ({ timeSec, parsedContent, rawContent, successful }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    const contentToCopy = parsedContent ? JSON.stringify(parsedContent, null, 2) : rawContent || '';
    if (contentToCopy) {
      navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusConfig = {
    true: {
      borderColor: 'border-l-green-500',
      icon: '✓',
      badge: 'Success',
      badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    },
    false: {
      borderColor: 'border-l-red-500',
      icon: '✗',
      badge: 'Failed',
      badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    },
    null: {
      borderColor: 'border-l-gray-400',
      icon: '○',
      badge: '',
      badgeClass: '',
    },
  }[String(successful) as 'true' | 'false' | 'null'];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-3">
      <div
        className={`border-l-4 ${statusConfig.borderColor} bg-slate-50/50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span
                className="text-slate-600 dark:text-slate-400 transition-transform"
                style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
              <span className="text-sm">{statusConfig.icon}</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Tool Result
              </span>
              {statusConfig.badge && (
                <Badge variant="outline" className={`text-xs ${statusConfig.badgeClass}`}>
                  {statusConfig.badge}
                </Badge>
              )}
              {timeSec !== null && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {timeSec.toFixed(2)}s
                </span>
              )}
            </div>
            {(parsedContent || rawContent) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                title="Copy result"
              >
                {copied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800">
            {parsedContent ? (
              <ScrollArea className="max-h-96">
                <pre className="text-xs font-mono bg-white dark:bg-slate-950 p-3 rounded border border-slate-200 dark:border-slate-800 overflow-x-auto">
                  {JSON.stringify(parsedContent, null, 2)}
                </pre>
              </ScrollArea>
            ) : rawContent ? (
              <ScrollArea className="max-h-96">
                <pre className="text-xs font-mono bg-white dark:bg-slate-950 p-3 rounded border border-slate-200 dark:border-slate-800 overflow-x-auto whitespace-pre-wrap break-words">
                  {rawContent}
                </pre>
              </ScrollArea>
            ) : (
              <p className="text-xs text-muted-foreground italic">No result data</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

interface TranscriptViewerProps {
  transcript: StructuredTranscriptEntryType[] | null | undefined;
  isSuperAdmin: boolean;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ transcript, isSuperAdmin }) => {
  if (!transcript || transcript.length === 0) {
    return <p className="text-muted-foreground">No structured transcript available.</p>;
  }

  // Filter out unwanted transcript entries
  const filteredTranscript = transcript.filter((entry) => {
    if (entry.role === 'node_transition') return false;
    if (isSuperAdmin) return true;
    if (entry.role === 'tool_call_invocation') {
      return entry.name === 'transfer_call' || entry.name === 'end_call';
    }
    if (entry.role === 'tool_call_result') return false;
    return true;
  });

  // Merge consecutive agent entries
  const mergedTranscript: StructuredTranscriptEntryType[] = [];
  for (let i = 0; i < filteredTranscript.length; i++) {
    const curr = filteredTranscript[i];
    if (!curr) continue;
    const prev =
      mergedTranscript.length > 0 ? mergedTranscript[mergedTranscript.length - 1] : undefined;

    if (curr.role === 'agent' && prev?.role === 'agent') {
      const currUtterance = curr as PhoneCallResponse.Utterance;
      const prevUtterance = prev as PhoneCallResponse.Utterance;
      if ('content' in prevUtterance && 'content' in currUtterance) {
        mergedTranscript[mergedTranscript.length - 1] = {
          ...prevUtterance,
          content: `${prevUtterance.content || ''} ${currUtterance.content || ''}`,
        };
      }
      continue;
    }

    mergedTranscript.push(curr);
  }

  return (
    <div className="space-y-3">
      {mergedTranscript.map((entry: StructuredTranscriptEntryType, entryIdx: number) => {
        const key = `${entry.role}-${entryIdx}`;

        if (entry.role === 'tool_call_invocation') {
          const toolCallEntry = entry as PhoneCallResponse.ToolCallInvocationUtterance;

          if (!isSuperAdmin) {
            let label = '';
            if (toolCallEntry.name === 'transfer_call') {
              let destination = '';
              if (toolCallEntry.arguments) {
                try {
                  const args = JSON.parse(toolCallEntry.arguments);
                  if (args && (args.transfer_destination_number || args.destination)) {
                    destination = args.transfer_destination_number || args.destination;
                  }
                } catch {}
              }
              label = destination ? `Call transferred (${destination})` : 'Call transferred';
            }
            if (toolCallEntry.name === 'end_call') label = 'Call ended';

            return (
              <div key={key} className="flex justify-center">
                <div className="px-4 py-2 text-sm bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800/50 rounded-md">
                  <span className="italic">{label}</span>
                </div>
              </div>
            );
          }

          let parsedArgs: Record<string, unknown> | null = null;
          if (toolCallEntry.arguments) {
            try {
              parsedArgs = JSON.parse(toolCallEntry.arguments);
            } catch {}
          }
          const timeSec =
            'time_sec' in entry ? ((entry as { time_sec?: number }).time_sec ?? null) : null;
          const isSpecialCall =
            toolCallEntry.name === 'transfer_call' || toolCallEntry.name === 'end_call';

          return (
            <CollapsibleToolCall
              key={key}
              toolName={toolCallEntry.name || 'Unknown'}
              timeSec={timeSec}
              parsedArgs={parsedArgs}
              isSpecialCall={isSpecialCall}
            />
          );
        }

        if (entry.role === 'tool_call_result' && isSuperAdmin) {
          const toolResultEntry = entry as PhoneCallResponse.ToolCallResultUtterance;
          let parsedContent: Record<string, unknown> | null = null;
          const rawContent = toolResultEntry.content || null;
          if (toolResultEntry.content) {
            try {
              parsedContent = JSON.parse(toolResultEntry.content);
            } catch {}
          }
          const timeSec =
            'time_sec' in entry ? ((entry as { time_sec?: number }).time_sec ?? null) : null;
          const successful =
            'successful' in entry ? ((entry as { successful?: boolean }).successful ?? null) : null;

          return (
            <CollapsibleToolResult
              key={key}
              timeSec={timeSec}
              parsedContent={parsedContent}
              rawContent={rawContent}
              successful={successful}
            />
          );
        }

        const isUser = entry.role === 'user';
        const isAgent = entry.role === 'agent';
        if (!isUser && !isAgent) return null;

        let content = '';
        if ('content' in entry && entry.content) {
          content = entry.content;
        } else {
          content = 'No content';
        }

        let timeSec: number | null = null;
        if ('words' in entry && Array.isArray(entry.words) && entry.words.length > 0) {
          const firstWord = entry.words[0];
          if (firstWord && 'start' in firstWord && typeof firstWord.start === 'number') {
            timeSec = firstWord.start;
          }
        }

        return (
          <div key={key} className="flex w-full">
            <div className="w-full">
              <div
                className={`px-4 py-2.5 rounded-md border text-sm ${
                  isUser
                    ? 'bg-blue-50/50 dark:bg-blue-900/10 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-900/50'
                    : 'bg-slate-50 dark:bg-slate-900/30 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-800'
                }`}
              >
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {entry.role}
                  </span>
                  {timeSec !== null && timeSec !== undefined && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatTranscriptTime(timeSec)}
                    </span>
                  )}
                </div>
                <div className="leading-relaxed">{content}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
